/**
 * VehicleStatus - Etiqueta e seletor reutilizáveis do Status do veículo
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-02 v1.0.0 - Criação inicial (VehicleStatusBadge + VehicleStatusSelect).
 */
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useVehicleStatuses } from '@/hooks/useVehicleStatuses';
import { cn } from '@/lib/utils';

/** Bolinha com a cor do status (cor vem do catálogo, por isso style inline) */
function Dot({ color }: { color: string | null | undefined }) {
  return <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground" style={color ? { backgroundColor: color } : undefined} />;
}

/** Etiqueta do status; sem status cai em "Ativo" (padrão do banco) */
export function VehicleStatusBadge({ statusId, className }: { statusId?: string | null; className?: string }) {
  const { byId } = useVehicleStatuses(true);
  const status = byId.get(statusId || 'active');
  return (
    <Badge variant="outline" className={cn('h-5 gap-1.5 px-1.5 text-[11px] font-medium', className)}>
      <Dot color={status?.color} />
      {status?.label ?? 'Ativo'}
    </Badge>
  );
}

interface SelectProps {
  value: string | null;
  onChange: (id: string | null) => void;
  /** Mostra opção "Todos os status" (uso em filtro) */
  allowAll?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/** Seletor de status (formulário e filtro) */
export function VehicleStatusSelect({ value, onChange, allowAll, placeholder = 'Selecione o status', className, disabled }: SelectProps) {
  const { activeStatuses, isLoading } = useVehicleStatuses();
  return (
    <Select
      value={value ?? (allowAll ? '__all__' : undefined)}
      onValueChange={(v) => onChange(v === '__all__' ? null : v)}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="__all__">Todos os status</SelectItem>}
        {activeStatuses.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            <div className="flex items-center gap-2"><Dot color={s.color} /><span>{s.label}</span></div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
